import { createContext, useEffect, useReducer } from "react";
import { apiPost, apiGet, encodeBase64 } from "src/configs/globalFunctions";
import axios from "src/utils/axios";
import PropTypes from "prop-types";
// import { verify, JWT_SECRET } from "src/utils/jwt";
// import { accordionActionsClasses } from "@mui/material";
import Avatar from "src/assets/avatar.png";
import { PLAN_PERMS_EVENT } from "src/hooks/usePlanPermissions";
import {
  fetchAndCacheAffiliatePermissions,
  AFFILIATE_PERMS_CACHE_KEY,
} from "src/configs/affiliatePermissions";

const initialAuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isImpersonating: false,
  user: null,
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("app-token", accessToken);
    // localStorage.setItem(
    //   "app-token",
    //   // 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OTcsImlhdCI6MTcwMjM5NjczNywiZXhwIjoxNzMzOTMyNzM3fQ.ZU_0jCf5T4uvtCZH_HJvbviPr0QIWLGRpTG1iER4sG0' //Aparecida
    //   // 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MTc0LCJpYXQiOjE3MDEzNjc5NTgsImV4cCI6MTczMjkwMzk1OH0.WQ7uoEaRqHBip_wn7L_BRZAsJvokdt0nty6J6oJkUK0' //Twilio
    //   "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwiaWF0IjoxNjk2ODgwMzM3LCJleHAiOjE3Mjg0MTYzMzd9.q6gqlgSJCWSi20BWrta_vb_BF3Lqlpm81SN04AGv0VY" //Bento
    //   // "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6OTgsImlhdCI6MTcwNjU1ODM3MCwiZXhwIjoxNzM4MDk0MzcwfQ.1laYuHS66XH8D2BW1W54clRvh5ROpPNuL9sWg5oIucU" //Pereira
    // );
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("app-token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common.Authorization;
  }
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, isImpersonating, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      isImpersonating: !!isImpersonating,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  UPDATE_USER: (state, action) => {
    const { user } = action.payload;
    return { ...state, user: { ...state.user, ...user } };
  },
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialAuthState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  isImpersonating: false,
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const isInSupportMode = !!localStorage.getItem("__support_backup__");

        let accessToken = window.localStorage.getItem("accessToken");
        let user = JSON.parse(window.localStorage.getItem("user"));

        // if (accessToken && verify(accessToken, JWT_SECRET)) {
        if (accessToken) {
          setSession(accessToken);

          if (!isInSupportMode) {
            try {
              const data = await apiGet("/currentuser");
              if (data?.user) {
                user = {
                  ...data.user,
                  role: "admin",
                  avatar: data.user.avatar || Avatar,
                };
                localStorage.setItem("user", JSON.stringify(user));
                // plan_script veio fresco do servidor — invalida o cache de
                // permissões e avisa os componentes já montados para recalcular
                // a partir dele (o /currentuser resolve depois do mount).
                localStorage.removeItem("planPermissionsCache");
                window.dispatchEvent(new Event(PLAN_PERMS_EVENT));
                await fetchAndCacheAffiliatePermissions();
              }
            } catch (err) {
              if (err?.submitResult?.status === 401) {
                // Conta bloqueada ou token inválido — limpa sessão e força logout.
                // Sem isso o router despacha isAuthenticated: true com usuário em
                // cache e cria um loop infinito entre home e a redirect de 401.
                setSession(null);
                dispatch({
                  type: "INITIALIZE",
                  payload: { isAuthenticated: false, isImpersonating: false, user: null },
                });
                return;
              }
              // offline ou erro de rede — usa o usuário em cache
            }
          }

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              isImpersonating: isInSupportMode,
              user,
            },
          });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              isImpersonating: false,
              user: null,
            },
          });
        }
      } catch (err) {
        //  console.log("erro de login: ", err);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "user" && event.newValue) {
        const updatedUser = JSON.parse(event.newValue);
        dispatch({ type: "UPDATE_USER", payload: { user: updatedUser } });
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const login = async (email, password) => {
    let accessToken = "";
    let user = "";
    let loginErrorResponse = null;

    await apiPost("/authenticate/", {
      body: {
        email,
        password,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        platform: navigator.platform || '',
      },

      done: (resp) => {
        accessToken = resp.data.token;

        user = {
          ...resp.data.user,
          role: "admin",
          avatar: resp.data.user.avatar || Avatar,
        };

        // console.log("Login: ", user);

        if (accessToken) {
          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("app-token", accessToken);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.removeItem("planPermissionsCache");
          window.dispatchEvent(new Event(PLAN_PERMS_EVENT));
        }
      },

      error: (resp) => {
        loginErrorResponse = resp;
        console.log("erro", resp?.data);
      },
    });

    if (loginErrorResponse || !accessToken) {
      throw { submitResult: loginErrorResponse };
    }

    setSession(accessToken);
    await fetchAndCacheAffiliatePermissions();
    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });
  };

  const updateUser = (updates) => {
    const updatedUser = { ...state.user, ...updates };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    dispatch({ type: "UPDATE_USER", payload: { user: updatedUser } });
  };

  const logout = async () => {
    localStorage.removeItem("__support_backup__");
    localStorage.removeItem("planPermissionsCache");
    localStorage.removeItem(AFFILIATE_PERMS_CACHE_KEY);
    setSession(null);
    dispatch({ type: "LOGOUT" });
  };

  const register = async (email, name, password, rootId, origin) => {
    console.log("rootId register: ", rootId);
    let registerErrorResponse = null;

    await apiPost("/users", {
      body: { email, name, password, rootId, origin },
      done: (resp) => {
        window.location.href = (origin === "minipay" ? "/minipay" : "") + "/emailValidation/" + encodeBase64(email);

        // const response = await axios.post("/api/account/register", {
        //   email,
        //   name,
        //   password,
        // });
        // const { accessToken, user } = response.data;
        // let accessToken = "";
        // let user = "";
        // console.log("email: ", encodeBase64(email));
        // accessToken = resp.data.token;
        // user = {
        //   ...resp.data.user,
        //   role: "admin",
        //   avatar: "https://cdn1.iconfinder.com/data/icons/website-internet/48/website_-_male_user-512.png",
        // };
        // console.log("email: ", encodeBase64(email));
      },
      error: (resp) => {
        registerErrorResponse = resp;
      },
    });

    if (registerErrorResponse) {
      throw { submitResult: registerErrorResponse };
    }

    // window.localStorage.setItem("accessToken", accessToken);
    // dispatch({
    //   type: "REGISTER",
    //   payload: {
    //     user,
    //   },
    // });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        register,
        updateUser,
        isImpersonating: state.isImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
